import { Everything } from '@hit/amorphic-types';
import { property, supertypeClass } from '@haventech/amorphic';
import { DiagnosisShort } from './DiagnosisShort';
import { TreatmentType, TreatmentStatus, Recommendation, Frequency, TimePeriod } from '@hit/hit-api-client';
import { RiskFactors } from './RiskFactors';
import { Customer } from './Customer';
import { MedicalCondition } from './MedicalCondition';

@supertypeClass({ toClient: true, toServer: true })
export class MedicalTreatment extends Everything {
  @property({ getType: () => Customer })
  customer: Customer;

  @property({ getType: () => RiskFactors })
  riskFactors: RiskFactors;

  @property({ getType: () => MedicalCondition })
  medicalCondition: MedicalCondition;

  @property()
  treatmentType: TreatmentType;

  @property()
  optionSelected: boolean;

  @property()
  treatmentDesc: string;

  @property()
  treatmentStatus: TreatmentStatus;

  @property()
  medication: string;

  @property()
  dateLastSeen: Date;

  @property()
  timePeriod: TimePeriod;

  @property()
  recommendation: Recommendation;

  @property({ rule: ['numeric'] })
  treatmentAmt: number;

  @property()
  treatmentFrequencyMode: Frequency;

  @property()
  details: DiagnosisShort = null;

  constructor() {
    super();
  }

  static new(
    riskFactors: RiskFactors,
    medicalCondition: MedicalCondition,
    optionSelected: boolean,
    treatmentType: TreatmentType,
    treatmentDesc?: string,
    treatmentAmt?: number,
    frequency?: Frequency,
    timePeriod?: TimePeriod
  ) {
    return new MedicalTreatment().init(
      riskFactors,
      medicalCondition,
      optionSelected,
      treatmentType,
      treatmentDesc,
      treatmentAmt,
      frequency,
      timePeriod
    );
  }

  init(
    riskFactors: RiskFactors,
    medicalCondition: MedicalCondition,
    optionSelected: boolean,
    treatmentType: TreatmentType,
    treatmentDesc?: string,
    treatmentAmt?: number,
    frequency?: Frequency,
    timePeriod?: TimePeriod
  ) {
    this.riskFactors = riskFactors;
    this.medicalCondition = medicalCondition ? medicalCondition : this.medicalCondition;
    this.customer = this.riskFactors.customer;
    this.treatmentType = treatmentType;
    this.treatmentDesc = treatmentDesc;
    this.optionSelected = optionSelected;
    if (treatmentAmt) {
      this.treatmentAmt = treatmentAmt;
    }
    if (frequency) {
      this.treatmentFrequencyMode = frequency;
    }
    if (timePeriod) {
      this.timePeriod = timePeriod;
    }
    return this;
  }

  // Weekly frequency
  set weeklyFrequency(treatmentValue: string) {
    //send lower bound
    switch (treatmentValue) {
      case 'ONE_TO_TWO':
        this.treatmentAmt = 1;
        break;
      case 'THREE_TO_FOUR':
        this.treatmentAmt = 3;
        break;
      case 'FIVE_OR_MORE':
        this.treatmentAmt = 5;
        break;
    }
    this.treatmentDesc = treatmentValue;
    this.treatmentFrequencyMode = Frequency.OLI_USAGEFREQ_PERWEEK;
  }
  get weeklyFrequency(): string {
    return this.treatmentDesc;
  }

  // Treatment completed
  get treatmentCompleted(): Date {
    return this.dateLastSeen;
  }
  set treatmentCompleted(date: Date) {
    this.dateLastSeen = date;
    this.treatmentStatus = TreatmentStatus.OLI_TRTMNTSTAT_COMPLETED;
  }

  //asthma weekly treatment
  set asthmaWeeklyTreatment(weeklyTreatment: boolean) {
    if (weeklyTreatment) {
      this.treatmentAmt = 3;
      this.treatmentFrequencyMode = Frequency.OLI_USAGEFREQ_PERWEEK;
    } else {
      this.treatmentAmt = null;
      this.treatmentFrequencyMode = null;
    }
  }
  get asthmaWeeklyTreatment(): boolean {
    return !!this.treatmentAmt;
  }

  // setPhysicianName
  get treatmentPhysicianName(): string {
    return this.details && this.details.physician;
  }
  set treatmentPhysicianName(name: string) {
    if (name) {
      this.details = new DiagnosisShort();
      this.details.physician = name;
    } else {
      this.details = null;
    }
  }

  get epilepsyOrSeizuresMedicationAmount() {
    return this.treatmentAmt;
  }

  set epilepsyOrSeizuresMedicationAmount(treatmentAmt: number) {
    this.treatmentAmt = treatmentAmt;
    this.treatmentStatus = TreatmentStatus.OLI_TRTMNTSTAT_INPROGRESS;
  }

  get asthmaSteroidsTreatmentAmt() {
    return this.treatmentAmt;
  }

  set asthmaSteroidsTreatmentAmt(treatmentAmt: number) {
    this.treatmentAmt = treatmentAmt;
    this.timePeriod = TimePeriod.WITHIN_ONE_YEAR;
  }
}
